import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpFromLine, Truck, Check, FileText, Users } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { ChannelIcon } from '../components/ChannelIcon'
import { useAuth } from '../contexts/auth'
import { useTenant, useActiveTenant } from '../contexts/tenant'
import { DashboardEscalas } from '../components/DashboardEscalas'

type ChannelCount = { channel: string; count: number }
type DayCount = { day: string; count: number }
type Dashboard = {
  aEnviar: number; enviados: number; confirmados: number; nfs: number
  aEnviarPorCanal: ChannelCount[]; enviadosPorCanal: ChannelCount[]; enviadosPorDia: DayCount[]
}
type MultiRow = { tenantId: string; name: string; aEnviar: number; feitosMes: number }

// ── helpers de data ──────────────────────────────────────────────────────────
const isoDia = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function mesCorrente() {
  const n = new Date()
  return { from: isoDia(new Date(n.getFullYear(), n.getMonth(), 1)), to: isoDia(n) }
}
function mesPassado() {
  const n = new Date()
  return {
    from: isoDia(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
    to: isoDia(new Date(n.getFullYear(), n.getMonth(), 0)),
  }
}

function monthOptions(): { value: string; label: string }[] {
  const now = new Date()
  const out: { value: string; label: string }[] = []
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) })
  }
  return out
}
function monthRange(month: string) {
  const [y, m] = month.split('-').map(Number)
  return { from: `${month}-01`, to: `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}` }
}

// ── componentes base ─────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color }: {
  label: string; value: number; icon: React.ElementType; color: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-5">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function CanalBars({ rows, barClass }: { rows: ChannelCount[]; barClass: string }) {
  if (!rows.length) return <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum pedido no período</p>
  const max = Math.max(...rows.map(r => r.count), 1)
  return (
    <ul className="space-y-2.5 px-5 py-4">
      {rows.map(r => (
        <li key={r.channel} className="flex items-center gap-3">
          <div className="flex w-36 shrink-0 items-center gap-1.5">
            <ChannelIcon name={r.channel} />
            <span className="truncate text-sm text-gray-700">{r.channel}</span>
          </div>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
            <div className={`h-full rounded-full ${barClass}`} style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900">{r.count}</span>
        </li>
      ))}
    </ul>
  )
}

// Gráfico de notas por dia (barras) com linha de média.
// ── Dashboard completo (cliente e admin de um cliente) ───────────────────────
function FullDashboard({ tenantId, isAdmin }: { tenantId: string; isAdmin: boolean }) {
  // UM filtro para a tela inteira. Abre no mês corrente (a leitura de sempre) e
  // os atalhos cobrem o dia — antes eram dois recortes na mesma página, um por
  // mês em cima e outro por período embaixo, o que dava números diferentes lado
  // a lado sem explicação.
  const mesAtual = mesCorrente()
  const [from, setFrom] = useState(mesAtual.from)
  const [to, setTo] = useState(mesAtual.to)
  const [canal, setCanal] = useState('')

  const { data, isLoading } = useQuery<Dashboard>({
    queryKey: ['dashboard', tenantId, from, to, canal],
    queryFn: async () => (await api.get('/outbound/dashboard', {
      params: { tenantId, from, to, ...(canal ? { channel: canal } : {}) },
    })).data,
    enabled: !!tenantId,
  })

  const d = data ?? { aEnviar: 0, enviados: 0, confirmados: 0, nfs: 0, aEnviarPorCanal: [], enviadosPorCanal: [], enviadosPorDia: [] }
  const canais = [...new Set([...d.aEnviarPorCanal, ...d.enviadosPorCanal].map(c => c.channel))]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Dashboard</h2>
        <p className="text-sm text-gray-500">Expedição do período · por canal</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">De</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Até</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Canal</label>
            <select value={canal} onChange={e => setCanal(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Todos</option>
              {canais.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-1">
            {([
              ['Hoje', () => { const h = isoDia(new Date()); setFrom(h); setTo(h) }],
              ['Ontem', () => { const o = isoDia(new Date(Date.now() - 86400000)); setFrom(o); setTo(o) }],
              ['7 dias', () => { setFrom(isoDia(new Date(Date.now() - 6 * 86400000))); setTo(isoDia(new Date())) }],
              ['Mês atual', () => { const m = mesCorrente(); setFrom(m.from); setTo(m.to) }],
              ['Mês passado', () => { const m = mesPassado(); setFrom(m.from); setTo(m.to) }],
              ['90 dias', () => { setFrom(isoDia(new Date(Date.now() - 89 * 86400000))); setTo(isoDia(new Date())) }],
            ] as const).map(([t, fn]) => (
              <button key={t} onClick={fn}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:border-blue-300 hover:text-blue-600">
                {t}
              </button>
            ))}
          </div>
          <span className="ml-auto text-xs text-gray-400">
            {from === to ? `dia ${from.split('-').reverse().join('/')}` : `${from.split('-').reverse().join('/')} a ${to.split('-').reverse().join('/')}`}
          </span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="A enviar (agora)"      value={d.aEnviar}     icon={ArrowUpFromLine} color="bg-orange-500" />
        <StatCard label="Enviados no período"        value={d.enviados}    icon={Truck}           color="bg-emerald-500" />
        <StatCard label="Confirmados (bipados)"  value={d.confirmados} icon={Check}           color="bg-blue-500" />
        <StatCard label="Notas fiscais no mês"   value={d.nfs}         icon={FileText}        color="bg-purple-500" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">A enviar por canal</span>
            <a href="/orders" className="text-xs text-blue-600 hover:underline">Ver pedidos</a>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando…</p>
              : <CanalBars rows={d.aEnviarPorCanal} barClass="bg-orange-400" />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">Enviados por canal</span>
            <span className="text-xs text-gray-400">no período</span>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando…</p>
              : <CanalBars rows={d.enviadosPorCanal} barClass="bg-emerald-400" />}
          </CardContent>
        </Card>
      </div>

      {/* Dia a dia, produtos, estados, transportadoras e o mapa — todos sob o
          mesmo filtro do topo. */}
      <DashboardEscalas tenantId={tenantId} from={from} to={to} canal={canal} />
    </div>
  )
}

// ── Dashboard do agente (enxuto: a enviar + enviados, filtro de data livre) ──
function AgentDashboard({ tenantId }: { tenantId: string }) {
  const now = new Date()
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)))
  const [to, setTo] = useState(iso(now))

  const { data } = useQuery<Dashboard>({
    queryKey: ['dashboard-agent', tenantId, from, to],
    queryFn: async () => (await api.get('/outbound/dashboard', { params: { tenantId, from, to } })).data,
    enabled: !!tenantId,
  })
  const d = data ?? { aEnviar: 0, enviados: 0, confirmados: 0, nfs: 0, aEnviarPorCanal: [], enviadosPorCanal: [], enviadosPorDia: [] }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500">A despachar · enviados</p>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-2" title="De" />
          <span className="text-gray-400">–</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-2" title="Até" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard label="A enviar (agora)"       value={d.aEnviar}  icon={ArrowUpFromLine} color="bg-orange-500" />
        <StatCard label="Enviados (no período)"  value={d.enviados} icon={Truck}           color="bg-emerald-500" />
      </div>
    </div>
  )
}

// ── Dashboard multi-cliente (admin, sem cliente selecionado) ─────────────────
function MultiClientDashboard() {
  const { setActiveTenantId } = useTenant()
  const months = monthOptions()
  const [month, setMonth] = useState(months[0].value)
  const { from, to } = monthRange(month)

  const { data: rows = [], isLoading } = useQuery<MultiRow[]>({
    queryKey: ['dashboard-multi', from, to],
    queryFn: async () => (await api.get('/outbound/dashboard-multi', { params: { from, to } })).data,
  })
  const totAEnviar = rows.reduce((s, r) => s + r.aEnviar, 0)
  const totFeitos = rows.reduce((s, r) => s + r.feitosMes, 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Visão geral — todos os clientes</h2>
          <p className="text-sm text-gray-500">Fila a enviar hoje e o que já foi feito no mês · clique num cliente para abrir</p>
        </div>
        <select value={month} onChange={e => setMonth(e.target.value)}
          className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none">
          {months.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard label="A enviar hoje (todos)" value={totAEnviar} icon={ArrowUpFromLine} color="bg-orange-500" />
        <StatCard label="Feitos no mês (todos)" value={totFeitos}  icon={FileText}        color="bg-purple-500" />
      </div>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Por cliente</span>
          <span className="text-xs text-gray-400"><Users className="mr-1 inline h-3.5 w-3.5" />{rows.length} ativo(s)</span>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando…</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum cliente ativo</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Cliente</th>
                  <th className="px-5 py-2.5 text-right font-medium">A enviar hoje</th>
                  <th className="px-5 py-2.5 text-right font-medium">Feitos no mês</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.tenantId} onClick={() => setActiveTenantId(r.tenantId)}
                    className="cursor-pointer hover:bg-blue-50/60">
                    <td className="px-5 py-2.5 font-medium text-gray-800">{r.name}</td>
                    <td className={`px-5 py-2.5 text-right font-bold tabular-nums ${r.aEnviar > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{r.aEnviar}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-600">{r.feitosMes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const activeTenantId = useActiveTenant()
  const role = user?.role

  if (role === 'admin' && !activeTenantId) return <MultiClientDashboard />
  if (role === 'agent') {
    if (!activeTenantId) return <SelecioneCliente />
    return <AgentDashboard tenantId={activeTenantId} />
  }
  if (!activeTenantId) return <SelecioneCliente />
  return <FullDashboard tenantId={activeTenantId} isAdmin={role === 'admin'} />
}

function SelecioneCliente() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Dashboard</h2>
        <p className="text-sm text-gray-500">Resumo operacional</p>
      </div>
      <div className="flex h-48 items-center justify-center rounded-xl border-2 border-dashed border-gray-200 text-center">
        <div>
          <p className="font-medium text-gray-600">Selecione um cliente no menu lateral</p>
          <p className="mt-1 text-sm text-gray-400">para visualizar o dashboard operacional</p>
        </div>
      </div>
    </div>
  )
}
